<script>
import { useAlert } from 'dashboard/composables';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

export default {
  components: {
    EmojiOrIcon,
    NextButton,
    InlineInput,
  },
  props: {
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      required: true,
    },
    emoji: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      isEditing: false,
      editValue: '',
    };
  },
  methods: {
    async onCopy(e) {
      e.preventDefault();
      await copyTextToClipboard(this.value);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    startEditing() {
      if (!this.editable) return;
      this.editValue = this.value || '';
      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.editInput?.focus();
      });
    },
    saveEdit() {
      if (!this.isEditing) return;
      this.isEditing = false;
      const trimmed = this.editValue.trim();
      if (trimmed !== (this.value || '')) {
        this.$emit('update', trimmed);
      }
    },
    cancelEdit() {
      this.isEditing = false;
    },
  },
};
</script>

<template>
  <dt class="flex items-center gap-1.5 text-polaris-muted-foreground">
    <EmojiOrIcon
      :icon="icon"
      :emoji="emoji"
      icon-size="14"
      class="flex-shrink-0"
    />
    <span class="truncate">{{ title }}</span>
  </dt>
  <dd class="group/row relative flex min-w-0 items-center">
    <!-- Inline edit mode -->
    <InlineInput
      v-if="isEditing"
      ref="editInput"
      v-model="editValue"
      :placeholder="title"
      class="!w-fit min-w-0"
      @enter-press="saveEdit"
      @escape-press="cancelEdit"
      @blur="saveEdit"
    />

    <!-- Read mode with link -->
    <a
      v-else-if="href"
      :href="href"
      class="min-w-0 truncate text-n-slate-11 hover:underline"
      :title="value"
    >
      {{ value }}
    </a>
    <span v-else-if="value" v-dompurify-html="value" class="min-w-0 truncate" />
    <span v-else class="min-w-0 truncate text-n-slate-11">
      {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
    </span>

    <!-- Copy and edit sit over the end of the value instead of beside it, so the value gets the whole column.
         They show on hover and on keyboard focus (opacity, not visibility, so Tab still reaches them), and
         stay shown where there is no hover (touch). -->
    <div
      v-if="!isEditing && ((showCopy && value) || editable)"
      class="absolute inset-y-0 end-0 flex items-center bg-polaris-card opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100 [@media(hover:none)]:opacity-100"
    >
      <NextButton
        v-if="showCopy && value"
        ghost
        xs
        slate
        class="shrink-0"
        icon="i-lucide-clipboard"
        :aria-label="$t('CONTACT_PANEL.COPY_VALUE', { title })"
        @click="onCopy"
      />
      <NextButton
        v-if="editable"
        ghost
        xs
        slate
        class="shrink-0"
        icon="i-lucide-pencil"
        :aria-label="$t('CONTACT_PANEL.EDIT_VALUE', { title })"
        @click.prevent="startEditing"
      />
    </div>
  </dd>
</template>
